<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref, watch, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { designThemes } from '@/components/design/themes';

// Directly import Vue components (this handles registration implicitly with <script setup>)
import GlobalSearchModal from '@/components/GlobalSearchModal.vue';
import VaultUploadDialogHost from '@/components/file_vault/VaultUploadDialogHost.vue';
import ConfettiEffect from '@/components/ConfettiEffect.vue';
import DownloadsPanel from '@/components/DownloadsPanel.vue';
import DemoSeedPanel from '@/components/DemoSeedPanel.vue';
import GaiaCornerStack from '@/components/design/composite/GaiaCornerStack.vue';
import ChangelogModal from '@/components/ChangelogModal.vue';
import TourOverlay from '@/components/tour/TourOverlay.vue';
import GaiaToastHost from '@/components/design/composite/GaiaToastHost.vue';
import OrganisationChangedModal from '@/components/design/domain/organisation/OrganisationChangedModal.vue';
import { useGlobalSearch } from '@/composables/useGlobalSearch';
import { resolveLayoutComponent } from '@/layouts';
import { useAuthStore } from '@/stores/auth';
import { useChangelogStore } from '@/stores/changelogStore';
import { useDesignStore } from '@/stores/designStore';
import { useSessionGuardStore } from '@/stores/sessionGuardStore';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { registerRecaptcha } from '@/utils/recaptcha';

const authStore = useAuthStore();
const changelogStore = useChangelogStore();
const sessionGuardStore = useSessionGuardStore();

// Hand the inject-only recaptcha composition to the module-scope service so
// stores and non-component code can execute challenges (see utils/recaptcha).
registerRecaptcha(useReCaptcha());

// Global search
const globalSearch = useGlobalSearch();
provide('globalSearch', globalSearch);

// Cmd/Ctrl+K opens global search. Lives here rather than in the sidebar: the
// shortcut is app-wide and must not depend on which chrome happens to render —
// but the modal only searches signed-in endpoints, so the public pages (login,
// verify-email, invite) must not be able to summon it.
function handleGlobalSearchShortcut(event: KeyboardEvent): void {
  if (!(event.metaKey || event.ctrlKey)) return;
  if (event.key.toLowerCase() !== 'k') return;
  if (!authStore.isAuthenticated) return;

  event.preventDefault();
  globalSearch.open();
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalSearchShortcut, true);
});
onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalSearchShortcut, true);
});

// Confetti component reference, exposed to feature flows (e.g. file-drop
// actions) via provide/inject so they can fire celebrations.
const confettiRef = ref<InstanceType<typeof ConfettiEffect>>();
provide('triggerConfetti', () => confettiRef.value?.triggerConfetti());
const route = useRoute();

const designStore = useDesignStore();

const activeDesignTheme = computed(() => designThemes[designStore.selectedTheme]);
const theme = computed(() => activeDesignTheme.value.base);
const themeOverrides = computed(() => activeDesignTheme.value.overrides);

// The theme owns the app font. naive only pushes common.fontFamily onto <body>
// through <n-global-style/>, which would also stamp body color, background,
// font-size and line-height — far more than we want here — so mirror just the
// family onto the CSS var <body> already reads, and keep it in step when the
// theme is switched.
//
// This has to be an inline style on <html>, not CSS: PrimeVue's theme.css also
// declares `:root { --font-family: "Inter var" }`, and main.ts imports it after
// App.vue, so at equal specificity PrimeVue wins on document order. An inline
// property beats both :root rules regardless of import order.
watchEffect((): void => {
  const family = themeOverrides.value.common?.fontFamily ?? theme.value.common?.fontFamily;
  if (family) document.documentElement.style.setProperty('--font-family', family);
});

// Dark-mode is one bit of the 4-way theme choice; provided for anything that
// only needs light/dark (e.g. a logo swap), not the naive/gaia axis.
const isDarkMode = computed(() => designStore.selectedTheme.endsWith('-dark'));
provide('isDarkMode', isDarkMode);

// Flips the dark bit while preserving whether naive or gaia was selected.
const toggleTheme = (): void => {
  const family = designStore.selectedTheme.startsWith('gaia') ? 'gaia' : 'naive';
  designStore.selectedTheme = `${family}-${isDarkMode.value ? 'light' : 'dark'}`;
};
provide('toggleTheme', toggleTheme);

// Sync dark mode with HTML class
watch(
  isDarkMode,
  (value: boolean): void => {
    if (value) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },
  { immediate: true }
);

const layoutComponent = computed(() => resolveLayoutComponent(route.meta.layout));
</script>

<template>
  <n-config-provider :theme="theme" :theme-overrides="themeOverrides">
    <n-loading-bar-provider>
      <n-dialog-provider>
        <!-- The layout owns the router-view; resolved from route meta + host -->
        <component :is="layoutComponent" v-if="route.matched.length" />

        <!-- Global confetti, fired by feature flows via the injected trigger -->
        <ConfettiEffect ref="confettiRef" />

        <GlobalSearchModal />

        <!-- Global bottom-right progress panels (Drive-style): GaiaCornerStack
               owns the fixed corner so simultaneous jobs never overlap -->
        <gaia-corner-stack>
          <demo-seed-panel />
          <downloads-panel />
        </gaia-corner-stack>

        <!-- Changelog: auto-opened by the boot check (the sidebar entry that opened
               it manually is pending the sidebar rebuild) -->
        <changelog-modal
          v-model:show="changelogStore.isOpen"
          :changelogs="changelogStore.prefetchedChangelogs ?? undefined"
        />

        <!-- Onboarding tours: spotlight overlay + step popover (teleports to body) -->
        <tour-overlay />

        <!-- Top-right toast stack (useGaiaToast) — teleports to body. -->
        <gaia-toast-host />

        <vault-upload-dialog-host />

        <!-- Blocks the page once the active organisation changed outside this tab -->
        <organisation-changed-modal :show="sessionGuardStore.organisationChanged" />
      </n-dialog-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>

<style>
:root {
  /* Core brand colors */
  --primary-color: #2563eb;
  --primary-hover: #1d4ed8;
  --primary-pressed: #1e40af;

  /* Brand colors */
  --accent-color: #7c3aed;
  --accent-hover: #8b5cf6;
  --brand-blue-dark: #0f172a;
  --brand-blue-medium: #1e40af;
  --brand-blue-light: #3b82f6;

  /* Neutral colors */
  --neutral-light: #f8fafc;
  --neutral-medium: #94a3b8;
  --neutral-dark: #334155;

  /* Typography — declared value only. The effective one is set as an inline
     property on <html> from the active naive theme's common.fontFamily (see the
     script above), because PrimeVue's theme.css also defines --font-family on
     :root and is imported later. Keep this in step with gaia-*.json anyway. */
  --font-family: 'Geist', system-ui, sans-serif;
}

/* Base styles */
body {
  margin: 0;
  padding: 0;
  /* Single source for the app font — naive components inherit font-family. */
  font-family: var(--font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Animations */
html {
  scroll-behavior: smooth;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
